import { ChangeEvent, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import { EditInlineDialogProps } from "components/Actions/dialogs/types";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { useStringInputValidation } from "shared/hooks/useStringInputValidation";
import { maxDialogWidth } from "shared/styles";
import { ENDPOINTS } from "shared/types/enums";

import { PAYLOAD_NAMES, PayloadData } from "../types";

const EditCompanyInline = ({ data, isOpen, close }: EditInlineDialogProps) => {
    const { triggerRootCompany } = useContext(ParamsContext);
    const { isInternalIntegrationUser } = useContext(AuthContext);

    const { updateData, handleResponse } = useApi();
    const { t } = useTranslation();
    const { toMd, fromMd } = useMediaQueries();

    const [isLoading, setIsLoading] = useState(false);

    const [payloadData, setPayloadData] = useState<PayloadData>({
        [PAYLOAD_NAMES.Company]: "",
        [PAYLOAD_NAMES.Code]: ""
    });

    const { company, code } = payloadData;

    const { stringInputError } = useStringInputValidation(code);

    useEffect(() => {
        if (data) {
            const { name, code } = data;

            setPayloadData({ company: name, code: code ?? "" });
        }
    }, [data]);

    const handleSubmit = async () => {
        const payload = {
            name: company,
            ...(isInternalIntegrationUser ? { code: code.trim() } : {})
        };

        try {
            setIsLoading(true);

            const response = await updateData(
                ENDPOINTS.Companies,
                data.id,
                payload
            );

            triggerRootCompany(false);
            close();
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    const handleNameValueChange = (e: ChangeEvent<HTMLInputElement>) =>
        setPayloadData({ ...payloadData, [e.target.name]: e.target.value });

    return (
        <Dialog
            isTitleSeparator
            isActionsSeparator
            title={t("General##edit company")}
            open={isOpen}
            close={close}
            submit={handleSubmit}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={
                        company.trim() && stringInputError === null
                    }
                />
            }
            extendToMaxWidth={toMd}
            css={css(fromMd && maxDialogWidth)}
        >
            <InputField
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                required
                readOnly={isLoading}
                value={company}
                name={PAYLOAD_NAMES.Company}
                onChange={handleNameValueChange}
                labelLeft={t("Dialog##name")}
                placeholder={t("Dialog##enter name")}
                customStyle={{ margin: "8px 0 16px 0" }}
            />

            {isInternalIntegrationUser && (
                <InputField
                    size={INPUT_FIELD_SIZES.Medium}
                    fullWidth
                    value={code}
                    readOnly={isLoading}
                    name={PAYLOAD_NAMES.Code}
                    onChange={handleNameValueChange}
                    labelLeft={t("Table##code")}
                    placeholder={t("Dialog##enter code")}
                    errorText={stringInputError}
                    customStyle={{ marginBottom: "16px" }}
                />
            )}
        </Dialog>
    );
};

export default EditCompanyInline;
